'use client';

import { create } from 'zustand';

interface LoginRequest {
  id: number;
  params: Record<string, string>;
}

export const useLoginDialogStore = create<{
  sequence: number;
  request: LoginRequest | null;
  open: (params: Record<string, string>) => void;
  close: () => void;
}>((set) => ({
  sequence: 0,
  request: null,
  open: (params) => set((state) => ({ sequence: state.sequence + 1, request: { id: state.sequence + 1, params } })),
  close: () => set({ request: null }),
}));

/** Keep existing login URL contracts while opening over the current page. */
export function requestLogin(loginHref = '/login'): void {
  const url = new URL(loginHref, 'https://localhost');
  useLoginDialogStore.getState().open(Object.fromEntries(url.searchParams));
}

export function isLoginHref(href: string): boolean {
  if (!href.startsWith('/') || href.startsWith('//')) return false;
  const url = new URL(href, 'https://localhost');
  return url.pathname === '/login' || (url.pathname === '/' && url.searchParams.get('login') === '1');
}

export interface LoginQueryRouter {
  isReady: boolean;
  prepareQueryCleanup?: () => Promise<unknown>;
  replace: (url: string, as: string, options: {shallow: boolean; scroll: boolean}) => Promise<boolean>;
  events: {
    on: (event: 'beforeHistoryChange' | 'routeChangeStart' | 'routeChangeComplete' | 'routeChangeError', callback: (...args: unknown[]) => void) => void;
    off: (event: 'beforeHistoryChange' | 'routeChangeStart' | 'routeChangeComplete' | 'routeChangeError', callback: (...args: unknown[]) => void) => void;
  };
}

/** Adapt the original AppProps.router, not Next's public context projection. */
export function pagesLoginQueryRouter(owner: LoginQueryRouter & {pageLoader: {
  getPageList: () => string[] | Promise<string[]>;
  getMiddleware: () => unknown;
}}): LoginQueryRouter {
  return {
    get isReady() { return owner.isReady; },
    replace: (url, as, options) => owner.replace(url, as, options),
    events: owner.events,
    prepareQueryCleanup: async () => {
      // Reuse the actual router's loader/cache/recovery, never a second build.
      // A rejected read happens before replace's full-document fallback.
      await Promise.all([owner.pageLoader.getPageList(), owner.pageLoader.getMiddleware()]);
    },
  };
}

// Pages Router may finish its initial query hydration after this modal mounts.
// The stable history key identifies the same consumed intent even if that
// transition briefly restores its original URL. A new link/request stays new.
const consumedEntries = new Map<string, string>();
const CONSUMED_INTENT_KEY = '__dobiConsumedLoginIntent';
function intentReceipt(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null;
  const value = (state as Record<string, unknown>)[CONSUMED_INTENT_KEY];
  if (!value || typeof value !== 'object') return null;
  const receipt = value as {version?: unknown; intent?: unknown};
  return receipt.version === 1 && typeof receipt.intent === 'string' ? receipt.intent : null;
}
// Pages replaces its history record while creating the router on a reload.
// This module is loaded with _app before that constructor; preserve only our
// receipt so that refreshing a dismissed, offline deep link cannot reopen it.
const initialConsumedIntent = (() => {
  try { return typeof window === 'undefined' ? null : intentReceipt(window.history.state); }
  catch { return null; }
})();
let initialEntryKey: string | null | undefined;

/** Consume the intent immediately; let the owning router commit its clean URL. */
export async function consumeLoginQuery(router?: LoginQueryRouter | null, canCommit = () => true): Promise<void> {
  const url = new URL(window.location.href);
  const previousState = window.history.state;
  const entryKey = typeof previousState?.key === 'string' ? previousState.key : null;
  if (initialEntryKey === undefined) initialEntryKey = entryKey;
  if (url.searchParams.get('login') !== '1') return;
  const intentParams = new URLSearchParams(url.searchParams);
  intentParams.sort();
  const intent = `${url.pathname}?${intentParams}${url.hash}`;
  const alreadyConsumed = intentReceipt(previousState) === intent
    || (entryKey === initialEntryKey && initialConsumedIntent === intent)
    || (entryKey !== null && consumedEntries.get(entryKey) === intent);
  if (!alreadyConsumed) {
    if (entryKey) {
      consumedEntries.set(entryKey, intent);
      if (consumedEntries.size > 64) consumedEntries.delete(consumedEntries.keys().next().value!);
    }
    requestLogin(`/login?${url.searchParams.toString()}`);
  }
  // Preserve the current router fields. This same-URL state receipt is not a
  // route change; the owning router still performs the URL/query update below.
  const markedState = {...previousState, [CONSUMED_INTENT_KEY]: {version: 1, intent}};
  window.history.replaceState(markedState, '', window.location.href);
  const sourceHref = `${url.pathname}${url.search}${url.hash}`;
  for (const key of ['login', 'entry', 'redirect', 'method', 'bind', 'source_landing']) url.searchParams.delete(key);
  const cleanHref = `${url.pathname}${url.search}${url.hash}`;
  if (router) {
    if (!router.isReady) return;
    // A query-only cleanup must never take Next's full-document fallback when
    // its route manifest is unavailable. Keep the usable sign-in form in place.
    await router.prepareQueryCleanup?.();
    if (!canCommit()) return;
    if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== sourceHref) return;
    if (entryKey && window.history.state?.key !== entryKey) return;
    await router.replace(cleanHref, cleanHref, {shallow: true, scroll: false});
    const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const currentState = window.history.state;
    if (currentHref === cleanHref && entryKey && currentState?.key === entryKey) {
      // Next owns url/as/options/key. Preserve any additional history metadata
      // without restoring its stale router fields over the completed replace.
      window.history.replaceState({...markedState, ...currentState,
        [CONSUMED_INTENT_KEY]: markedState[CONSUMED_INTENT_KEY]}, '', cleanHref);
    }
    return;
  }
  // App Router patches native history, so its tree/canonical URL stay in sync.
  window.history.replaceState(markedState, '', cleanHref);
}

/** Follow commits, including the initial Pages hydration that emits no complete event. */
export function installLoginQueryConsumer(
  router?: LoginQueryRouter | null,
  onError: (error: unknown) => void = () => {},
): () => void {
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending = false;
  let rescan = false;
  let navigationPending = false;
  const consume = () => {
    if (disposed || navigationPending) return;
    if (pending) { rescan = true; return; }
    pending = true;
    void consumeLoginQuery(router, () => !disposed && !navigationPending).catch((error) => {
      if (error && typeof error === 'object' && (error as {cancelled?: unknown}).cancelled === true) return;
      try { onError(error); } catch { /* Optional reporting cannot reject this cleanup. */ }
    }).finally(() => {
      pending = false;
      if (rescan) { rescan = false; schedule(); }
    });
  };
  const schedule = () => {
    if (disposed || timer !== undefined) return;
    // beforeHistoryChange fires before Next commits router state; read after it.
    timer = setTimeout(() => { timer = undefined; consume(); }, 0);
  };
  const onPopState = (event: PopStateEvent) => {
    const key = typeof event.state?.key === 'string' ? event.state.key : null;
    const receipt = intentReceipt(event.state);
    if (key && receipt) consumedEntries.set(key, receipt);
    schedule();
  };
  const onRouteStart = (destination: unknown) => {
    const url = new URL(window.location.href);
    for (const key of ['login', 'entry', 'redirect', 'method', 'bind', 'source_landing']) url.searchParams.delete(key);
    const cleanupHref = `${url.pathname}${url.search}${url.hash}`;
    // Our own query replace is expected. A user's different navigation always
    // wins over delayed optional cleanup, even before its URL has committed.
    if (!pending || destination !== cleanupHref) navigationPending = true;
  };
  const onRouteSettled = () => { navigationPending = false; schedule(); };
  consume();
  window.addEventListener('popstate', onPopState);
  window.addEventListener('online', schedule);
  router?.events.on('beforeHistoryChange', schedule);
  router?.events.on('routeChangeStart', onRouteStart);
  router?.events.on('routeChangeComplete', onRouteSettled);
  router?.events.on('routeChangeError', onRouteSettled);
  return () => {
    disposed = true;
    if (timer !== undefined) clearTimeout(timer);
    window.removeEventListener('popstate', onPopState);
    window.removeEventListener('online', schedule);
    router?.events.off('beforeHistoryChange', schedule);
    router?.events.off('routeChangeStart', onRouteStart);
    router?.events.off('routeChangeComplete', onRouteSettled);
    router?.events.off('routeChangeError', onRouteSettled);
  };
}
